/**
 * P6 — Agency portal-domains API client (subdomain + custom-domain / CNAME).
 *
 * Agency employee surface (Bearer = agency `emp_` key). Backend contract
 * (central-api Modules/ClientReporting — P2, consume exactly, do NOT change it;
 * all routes require `portal.domains.manage`):
 *
 *   GET    agency-domains
 *          → { data: AgencyDomain[] }
 *          (auto-provisions the {slug}.r.adup.io subdomain on first GET.)
 *   POST   agency-domains {hostname}
 *          → 201 { data: AgencyDomain }  (status pending_dns + verification_record)
 *   POST   agency-domains/{id}/verify
 *          → { data: AgencyDomain }      (re-checks TXT + Cloudflare when enabled)
 *   DELETE agency-domains/{id}           (soft-delete; NOT allowed on the primary
 *                                          subdomain — API 4xx's)
 *
 *   set-primary: P6 backend MAY add an endpoint to promote a verified custom
 *   domain to primary. If/when it lands, `setPrimaryAgencyDomain` below wires
 *   the assumed shape (POST agency-domains/{id}/primary). Until the backend
 *   confirms it, the UI keeps the "Make primary" action behind a guard — see
 *   the ENABLE_SET_PRIMARY flag in settings/PortalDomainsSection.tsx.
 */
import { request } from './client-reports-http';

// ---------- wire types ----------

export type AgencyDomainType = 'subdomain' | 'custom';

export type AgencyDomainStatus =
  | 'pending_dns'
  | 'verifying'
  | 'active'
  | 'failed'
  | 'disabled';

export interface DomainVerificationRecord {
  /** Currently always 'TXT' per the P2 contract; typed wide for CNAME support. */
  type: 'TXT' | 'CNAME';
  name: string;
  value: string;
}

export interface AgencyDomain {
  id: string;
  hostname: string;
  type: AgencyDomainType;
  status: AgencyDomainStatus;
  is_primary: boolean;
  tls_provider: string | null;
  tls_status: string | null;
  verified_at: string | null;
  created_at: string | null;
  /** Present while a custom domain still needs a DNS record added. */
  verification_record?: DomainVerificationRecord | null;
}

export interface AddAgencyDomainBody {
  hostname: string;
}

// ---------- API ----------

/**
 * List the org's portal domains. The backend auto-provisions the primary
 * `{slug}.r.adup.io` subdomain on the first call, so the response always
 * contains at least the subdomain row.
 */
export async function listAgencyDomains(): Promise<AgencyDomain[]> {
  const res = await request<{ data?: AgencyDomain[] }>('agency-domains', {
    method: 'GET',
  });
  return res.data ?? [];
}

/** Add a custom domain; returns the pending row incl. the TXT record to add. */
export async function addAgencyDomain(
  hostname: string
): Promise<AgencyDomain> {
  const res = await request<{ data: AgencyDomain }>('agency-domains', {
    method: 'POST',
    body: JSON.stringify({ hostname } as AddAgencyDomainBody),
  });
  return res.data;
}

/** Re-check the TXT record (+ Cloudflare status when enabled) for one domain. */
export async function verifyAgencyDomain(id: string): Promise<AgencyDomain> {
  const res = await request<{ data: AgencyDomain }>(
    `agency-domains/${encodeURIComponent(id)}/verify`,
    { method: 'POST' }
  );
  return res.data;
}

/** Soft-delete a custom domain. The primary subdomain cannot be deleted. */
export async function deleteAgencyDomain(id: string): Promise<void> {
  await request<unknown>(`agency-domains/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Promote a verified custom domain to primary. Backend: P6 shipped
 * `POST /api/v1/agency-domains/{id}/set-primary` (active-only; clears other
 * primaries) → magic-link + report emails move to the verified custom hostname.
 */
export async function setPrimaryAgencyDomain(
  id: string
): Promise<AgencyDomain> {
  const res = await request<{ data: AgencyDomain }>(
    `agency-domains/${encodeURIComponent(id)}/set-primary`,
    { method: 'POST' }
  );
  return res.data;
}
